import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, Send, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fields = [
  { name: "business_name", label: "Business name", placeholder: "Acme Plumbing", type: "input" },
  { name: "owner_name", label: "Owner / contact name", placeholder: "Jane Smith", type: "input" },
  { name: "phone", label: "Phone number", placeholder: "(555) 555-1234", type: "input", inputType: "tel" },
  { name: "email", label: "Email address", placeholder: "you@business.com", type: "input", inputType: "email" },
  { name: "business_address", label: "Business address", placeholder: "123 Main St, Springfield, IL", type: "input" },
  { name: "service_area", label: "Service area (cities/towns you serve)", placeholder: "Springfield, Chatham, Sherman", type: "textarea" },
  { name: "years_in_business", label: "Years in business", placeholder: "e.g. 7", type: "input" },
];

const initial = fields.reduce((acc, f) => ({ ...acc, [f.name]: "" }), {});

export const LeadForm = () => {
  const [values, setValues] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => {
    setValues((v) => ({ ...v, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const missing = fields.find((f) => !values[f.name]?.trim());
    if (missing) {
      toast.error(`Please fill in: ${missing.label}`);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, values);
      toast.success("Request received! We'll reach out within 2–3 business days.");
      setDone(true);
      setValues(initial);
      // Optional: log for debug
      console.log("Lead created:", res.data);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="demo"
      data-testid="lead-capture-section"
      className="py-20 md:py-28 bg-gray-900 text-white relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-widest text-blue-400 mb-3">
              Free website demo
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">
              See your future website — <span className="text-blue-400">before you pay a dime.</span>
            </h2>
            <p className="mt-5 text-gray-300 leading-relaxed">
              Fill this out and within 2–3 business days we'll send you a real, working
              preview of your custom website. If you love it, we launch it.
              If not, you owe us nothing.
            </p>

            <ul className="mt-8 space-y-3 text-sm text-gray-300">
              {[
                "No payment up front",
                "No long contracts",
                "We do the writing for you",
                "Direct line to your designer",
              ].map((p) => (
                <li key={p} className="flex items-center gap-2.5">
                  <span className="h-5 w-5 rounded-md bg-blue-700/30 text-blue-300 inline-flex items-center justify-center">
                    <Check size={14} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex items-center gap-3 text-xs text-gray-400">
              <ShieldCheck size={14} className="text-blue-400" />
              Your info is private. We never share or sell it.
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
            data-testid="lead-capture-form"
          >
            <div className="rounded-2xl bg-white text-gray-900 p-7 md:p-10 shadow-2xl border border-white/10">
              {done ? (
                <div className="text-center py-10" data-testid="lead-success-state">
                  <div className="mx-auto h-14 w-14 rounded-full bg-blue-700/10 text-blue-700 flex items-center justify-center">
                    <Check size={28} />
                  </div>
                  <h3 className="font-display font-extrabold text-2xl md:text-3xl mt-5">
                    Request received.
                  </h3>
                  <p className="mt-3 text-gray-600 max-w-md mx-auto">
                    Thanks! We've sent your details to our team. Expect to hear
                    from us within 2–3 business days with next steps for your free demo.
                  </p>
                  <Button
                    onClick={() => setDone(false)}
                    variant="outline"
                    className="mt-6"
                    data-testid="lead-submit-another"
                  >
                    Submit another business
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="lead-form-element">
                  <div className="font-display font-bold text-xl">
                    Tell us about your business
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    {fields.map((f) => (
                      <div
                        key={f.name}
                        className={f.type === "textarea" ? "md:col-span-2" : ""}
                      >
                        <Label
                          htmlFor={f.name}
                          className="text-xs font-semibold text-gray-700 uppercase tracking-wider"
                        >
                          {f.label}
                        </Label>
                        {f.type === "textarea" ? (
                          <Textarea
                            id={f.name}
                            name={f.name}
                            value={values[f.name]}
                            onChange={onChange}
                            placeholder={f.placeholder}
                            rows={3}
                            data-testid={`lead-input-${f.name}`}
                            className="mt-1.5 border-gray-300 focus-visible:ring-blue-700"
                          />
                        ) : (
                          <Input
                            id={f.name}
                            name={f.name}
                            value={values[f.name]}
                            onChange={onChange}
                            placeholder={f.placeholder}
                            type={f.inputType || "text"}
                            data-testid={`lead-input-${f.name}`}
                            className="mt-1.5 border-gray-300 focus-visible:ring-blue-700"
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="lead-submit-button"
                    className="w-full h-12 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-md group"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        Get my free website demo
                        <Send className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </Button>
                  <p className="text-[11px] text-gray-500 text-center">
                    By submitting you agree to be contacted about your demo. No spam — ever.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
